import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { validateInvite, type InviteInfo } from "@/features/invites";
import { signUpWithPasskey } from "@/features/passkey";

export const Route = createFileRoute("/join/$token")({
  component: JoinPage,
});

type LoadState =
  | { phase: "loading" }
  | { phase: "invalid"; message: string }
  | { phase: "ready"; info: InviteInfo };

const ROLE_LABEL: Record<string, string> = {
  manager: "Manager",
  staff: "Staff",
};

function JoinPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const info = await validateInvite(token);
      if (cancelled) return;
      setState(
        info.valid
          ? { phase: "ready", info }
          : {
              phase: "invalid",
              message: info.error ?? "This invite link is invalid or expired.",
            },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const join = async () => {
    setError(null);
    setBusy(true);
    try {
      await signUpWithPasskey(token, displayName.trim());
      // Account created + session cookie set. Full-page nav so AuthProvider
      // re-initialises with the new session and lands in the operator app
      // (TNK-177 — `/` now redirects to the storefront, so go straight to
      // /dashboard).
      window.location.href = "/dashboard";
    } catch (e) {
      if (e instanceof DOMException && e.name === "NotAllowedError") {
        setError("Passkey setup was cancelled. Try again when you're ready.");
      } else {
        setError(
          e instanceof Error ? e.message : "Could not create your account.",
        );
      }
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-5 rounded-lg border border-border bg-card p-6">
        {state.phase === "loading" && (
          <p className="text-center text-sm text-muted-foreground">
            Checking your invite…
          </p>
        )}

        {state.phase === "invalid" && (
          <div className="space-y-2 text-center">
            <h1 className="text-lg font-semibold text-foreground">
              Invite unavailable
            </h1>
            <p className="text-sm text-muted-foreground">{state.message}</p>
            <p className="text-[11px] text-muted-foreground">
              Ask the store owner for a fresh link.
            </p>
          </div>
        )}

        {state.phase === "ready" && (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-lg font-semibold text-foreground">
                Join {state.info.storeName}
              </h1>
              <p className="text-sm text-muted-foreground">
                You've been invited as{" "}
                <span className="font-semibold text-foreground">
                  {ROLE_LABEL[state.info.role ?? ""] ?? state.info.role}
                </span>
                . Create a passkey to set up your account — no password, no eBay
                needed.
              </p>
            </div>

            <label className="block space-y-1">
              <span className="text-[11px] font-medium text-muted-foreground">
                Your name
              </span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Alex"
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </label>

            <button
              type="button"
              onClick={() => void join()}
              disabled={busy || displayName.trim().length === 0}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Setting up…" : "Create passkey & join"}
            </button>

            {error && <p className="text-[11px] text-destructive">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
